<template>
	<div class="search-bar-container">
		<div
			class="search-bar"
			@click="onClick"
			:class="{ focused: isFocused, 'no-recents': recents.length === 0 }"
		>
			<div class="search-bar-interior">
				<div class="search-input-container" @click.stop="onClick">
					<SearchIcon class="search-bar-icon" />
					<i18n-t
						v-if="!showInputElement"
						keypath="search_display_term"
						tag="div"
						class="search-placeholder"
					>
						<template #Term>
							<strong v-text="q || mergedQueryParams.q" data-private />
						</template>
					</i18n-t>
					<input
						v-if="showInputElement"
						class="search-input"
						type="text"
						v-model="q"
						@keyup.enter="search(null)"
						ref="input"
						:placeholder="inputPlaceholder"
						@focus="isInputFocused = true"
						@blur="isInputFocused = false"
					/>
				</div>
				<div v-if="appliedFiltersCount" class="filter-count">
					<FilterFunnelIcon />
					{{ appliedFiltersCount }}
				</div>
				<CloseIcon
					type="regular"
					class="clear-search"
					v-if="q || appliedFiltersCount"
					@click.stop="clearQuery"
				/>
			</div>
			<div
				class="dropdown-overlay"
				:class="{
					expanded: isOpen,
					transitioning: isTransitioning,
					'datedropdown-open': isDateFilterOpen,
				}"
				@transitionstart="onTransitionStart"
				@transitionend="onTransitionEnd"
			>
				<div class="suggestions" v-if="showSuggestions">
					<div
						class="current-chat-suggestion"
						v-if="showChatSuggestion"
						@click.stop="onChatSuggestionClick"
					>
						<QuickSearchIcon />
						<div
							class="current-chat-label"
							v-text="$t('search_placeholder', [currentChat.displayname])"
						/>
					</div>
					<div
						class="filter-search-suggestion"
						@click.stop="onFilterSearchClick"
					>
						<FunnelOutlineIcon />
						<div
							class="current-chat-label"
							v-text="$t('search_filter_by_label')"
						/>
					</div>
				</div>
				<div class="recent-searches" v-if="showRecents">
					<div
						class="recent-searches-label"
						v-text="$t('search_recents_label')"
					/>
					<div
						class="recent-search"
						v-for="recent in recents"
						:key="recent.query"
						@click.stop="onClickRecent(recent)"
					>
						<span v-text="recent.query" />
						<CloseIcon
							class="remove-recent"
							type="regular"
							@click.stop="removeRecent(recent.query)"
						/>
					</div>
				</div>
			</div>
		</div>
		<div
			class="dropdown-backdrop"
			v-if="isOpen"
			@mousedown="isFocused = false"
		/>
	</div>
</template>

<script>
import store, {
	getChat,
	getCurrentChat,
	getOrganization,
	getUser,
} from '@/store'
import {
	SearchIcon,
	CloseIcon,
	QuickSearchIcon,
	FilterFunnelIcon,
	FunnelOutlineIcon,
} from '@/icons'
import { addEscapeListener, removeEscapeListener } from '@/helpers/escape'
import {
	getRecentSearches,
	removeRecentSearch,
	setSearchQueryParams,
} from './searchUtils'
import { watch } from 'vue'
import { parseQueryParamArray } from '@/helpers/utils'
import { closeAllDropdowns } from '@/components/common/Dropdown'
import router from '@/router'
import { listenForKeyboardShortcut } from '@/helpers/keyboard'

const queryKeys = ['q', 'chats', 'users', 'sd', 'ed', 'duration']

export default {
	name: 'SearchBar',
	components: {
		SearchIcon,
		CloseIcon,
		QuickSearchIcon,
		FilterFunnelIcon,
		FunnelOutlineIcon,
	},

	props: {
		queryParams: Object,
	},

	data() {
		const { q, chats, users, sd, ed, duration } = this.queryParams || {}
		return {
			isFocused: false,
			isInputFocused: false,
			recents: getRecentSearches(),
			org: {},
			isTransitioning: false,
			filterOpen: {},
			q: q || null,
			chats: parseQueryParamArray(chats),
			users: parseQueryParamArray(users),
			sd: sd || null,
			ed: ed || null,
			duration: duration || null,
			currentChat: store.currentChatId ? getCurrentChat() : null,
			savedQueryState: null,
			fromUsers: [],
		}
	},

	beforeUnmount() {
		this.unwatchChat?.()
		this.disableShortcut?.()
		removeEscapeListener(this.escapeId)
	},

	created() {
		this.org = getOrganization()

		this.unwatchChat = watch(
			() => store.currentChatId,
			(id) => {
				this.currentChat = id ? getChat(id) : null
			}
		)

		router.beforeEach((to, from) => {
			// have to listen to route changes to know when to reset the search state
			// if the user navigates to a search result (?centered=xxx) or to search
			// itself (/search), preserve state. Otherwise, reset it when navigating away.
			const isViewingResult = !!to.query.centered
			const isSearchRoute = to.name === 'search'
			const isLeavingSearch = to.name !== 'search' && from.name === 'search'

			if (isLeavingSearch) {
				const state = { ...from.query }
				delete state.centered
				this.savedQueryState = state
			} else if (isSearchRoute) {
				this.savedQueryState = null
			}
			if (!isSearchRoute && !isViewingResult) {
				this.resetState()
			}
			if (isSearchRoute && to.query.focus) {
				// set the input to focus
				setTimeout(() => {
					this.isFocused = true
					this.focusInput()
				}, 500)
				// remove the focus flag to it doesn't get reused later
				const query = { ...to.query }
				delete query.focus
				to.query = query
				to.replace = true
				return to
			}
		})
	},

	mounted() {
		this.$onEventBus({
			clearSearchQuery: () => {
				setTimeout(() => {
					// wait for the route change to complete
					this.resetState()
				}, 250)
			},
			OrgUpdated: () => {
				this.org = getOrganization()
			},
			'my-user-update': () => {
				this.recents = getRecentSearches()
			},
			'from-filter-users': (users) => {
				this.fromUsers = users || []
			},
		})
		this.disableShortcut = listenForKeyboardShortcut('CMD+F', () => {
			this.isFocused = true
			this.focusInput()
		})
	},

	computed: {
		isAnyFilterOpen() {
			return Object.values(this.filterOpen).some((x) => x)
		},
		showRecents() {
			return this.recents?.length > 0 && !this.isAnyFilterOpen
		},
		isOpen() {
			return this.isFocused
		},
		isSearchPage() {
			return this.$route.name === 'search'
		},
		showChatSuggestion() {
			return this.currentChat && !this.chats?.length && !this.isAnyFilterOpen
		},
		showInputElement() {
			if (this.isFocused) return true
			if (this.q || this.mergedQueryParams.q) return false
			return true
		},
		showSuggestions() {
			return this.showChatSuggestion || !this.isSearchPage
		},
		isReadyToSearch() {
			const params = this.getQueryParams()
			return Object.keys(params).length > 0
		},
		inputPlaceholder() {
			const chats =
				this.chats ||
				this.mergedQueryParams.chats?.split(',').map(parseFloat) ||
				[]
			const users =
				this.users ||
				this.mergedQueryParams.users?.split(',').map(parseFloat) ||
				[]
			if (!chats.length && !users.length) {
				// No user filters, no chat filters: “Search in {org name}”
				return this.$t('search_placeholder', [this.org.name])
			}
			if (chats.length === 1 && !users.length) {
				// One chat filter, no user filters: “Search in {chat name}”
				const chat = getChat(chats[0])
				return chat
					? this.$t('search_placeholder', [chat.displayname])
					: this.$t('common_search')
			}
			if (!chats.length && users.length === 1) {
				// One user filter, no chat filters: “Search from {user name}”
				const user =
					this.fromUsers?.find((u) => u.id === users[0]) ||
					getUser(users[0]) ||
					null
				return user
					? this.$t('search_placeholder_from', { User: user.fullname })
					: this.$t('common_search')
			}
			// Everything else: “Search”
			return this.$t('common_search')
		},
		isDateFilterOpen() {
			return this.filterOpen['date']
		},
		appliedFiltersCount() {
			const chatsCount = this.mergedQueryParams?.chats
				? this.mergedQueryParams.chats.split(',').filter(Boolean).length
				: 0

			const usersCount = this.mergedQueryParams?.users
				? this.mergedQueryParams.users.split(',').filter(Boolean).length
				: 0

			const dateOrDurationFilter = Boolean(
				this.mergedQueryParams?.sd ||
					this.mergedQueryParams?.ed ||
					this.mergedQueryParams?.duration
			)
				? 1
				: 0

			return chatsCount + usersCount + dateOrDurationFilter
		},
		mergedQueryParams() {
			return Object.assign({}, this.queryParams, this.savedQueryState || {})
		},
	},

	methods: {
		onClick() {
			if (this.savedQueryState && !this.isSearchPage) {
				this.goBackToPreviousResults()
			} else {
				this.isFocused = true
				this.$nextTick(() => this.focusInput())
			}
		},
		onClickRecent(queryData) {
			if (this.isReadyToSearch) {
				// if user already has filters selected, just use the query text
				this.q = queryData.query
				this.search()
			} else {
				// otherwise use all of the params from the saved search
				this.q = queryData.query
				this.search(queryData)
			}
		},
		search(queryData, shouldFocus) {
			this.isFocused = false

			if (this.isSearchPage) {
				// if already in search, just update query param
				this.updateQuery(queryData, shouldFocus)
			} else {
				// otherwise, navigate to search page
				this.navToSearch(queryData, shouldFocus)
			}
		},
		resetState() {
			this.q = null
			this.sd = null
			this.ed = null
			this.users = null
			this.chats = null
			this.duration = null
			this.sort = null
			this.searchType = null
			this.isFocused = false
			this.savedQueryState = null
		},
		async removeRecent(text) {
			this.q = null
			this.recents = this.recents.filter((r) => r.query !== text)
			this.recents = await removeRecentSearch(text)
			this.focusInput()
		},
		navToSearch(queryData, shouldFocus) {
			if (this.isSearchPage) return
			const query = queryData
				? this.getQueryParamsFromRecent(queryData)
				: this.getQueryParams()
			if (shouldFocus) query.focus = 1
			this.$router.push({
				name: 'search',
				query: { ...this.$route.query, ...query },
			})
		},
		goBackToPreviousResults() {
			this.$router.go(-1)
		},
		updateQuery(queryData) {
			const query = queryData
				? this.getQueryParamsFromRecent(queryData)
				: this.getQueryParams()

			if (!Object.keys(query).length) return
			setSearchQueryParams({ ...this.$route.query, ...query }, true)
		},
		getQueryParams() {
			return queryKeys.reduce((acc, key) => {
				let val = this[key]
				if (val) {
					acc[key] = Array.isArray(val) ? val.join(',') : val
				} else if (this.$route.query[key]) {
					acc[key] = null
				}
				return acc
			}, {})
		},
		getQueryParamsFromRecent(queryData) {
			return Object.entries({
				q: queryData.query,
				users: queryData.user_ids,
				chats: queryData.bubble_ids,
				sort: queryData.orderby,
				duration: queryData.relative_date_duration,
				sd: queryData.start_date,
				ed: queryData.end_date,
			})
				.filter(([key, val]) => (Array.isArray(val) ? val.length > 0 : val))
				.reduce((acc, [key, val]) => {
					acc[key] = Array.isArray(val) ? val.join(',') : val
					return acc
				}, {})
		},
		focusInput() {
			const { input } = this.$refs
			if (!input) return
			input.focus()
		},
		clearQuery() {
			this.q = null
			this.sd = null
			this.ed = null
			this.users = null
			this.chats = null
			this.duration = ''
			this.sort = null
			this.searchType = null
			this.savedQueryState = null
			this.updateQuery()
		},
		onTransitionStart(e) {
			if (e.propertyName !== 'max-height') return
			this.isTransitioning = true
		},
		onTransitionEnd(e) {
			if (e.propertyName !== 'max-height') return
			this.isTransitioning = false
		},
		setFilterOpen(type, isOpen) {
			this.filterOpen[type] = isOpen
		},
		onChatSuggestionClick() {
			if (!this.currentChat) return
			const { id } = this.currentChat
			this.chats = this.chats || []
			if (this.chats.includes(id)) return
			this.chats.unshift(id)
			if (this.q) {
				this.search({ query: this.q, bubble_ids: this.chats })
			} else {
				this.search(null, true)
			}
		},
		dateFilterUpdate(dates) {
			this.duration = dates.duration
			this.sd = dates.sd
			this.ed = dates.ed
		},
		onFilterSearchClick() {
			this.isFocused = false
			if (this.q) {
				this.search({ query: this.q })
			} else {
				this.navToSearch()
			}
		},
	},
	watch: {
		queryParams(params) {
			this.chats = params.chats?.split(',').map(parseFloat) || null
			this.users = params.users?.split(',').map(parseFloat) || null
			this.sd = params.sd || null
			this.ed = params.ed || null
			this.duration = params.duration || null
		},

		isOpen(isOpen) {
			if (isOpen) {
				this.escapeId = addEscapeListener(() => {
					this.isFocused = false
				})
				closeAllDropdowns()
			} else {
				removeEscapeListener(this.escapeId)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

$bar-height: 30px;

@media screen and (max-width: 800px) {
	.search-bar-container {
		display: none !important;
	}
}

.search-bar-container {
	width: 100vw;
	height: $search-bar-height;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--sidebar-gray);
	border-bottom: 1px solid var(--neutral-40040);
}
.search-bar {
	display: flex;
	align-items: center;
	background: var(--neutral-0);
	height: $bar-height;
	width: 100%;
	max-width: 490px;
	position: relative;
	border-radius: 6px;
	border: 1px solid var(--neutral-40040);
	transition: border-radius, border-bottom-color 0.3s ease-in-out;
	cursor: pointer;
	&.focused {
		border-radius: 6px 6px 0 0;
		border-bottom-color: transparent;
		.search-input-container {
			justify-content: flex-start;
		}
	}
	&.no-recents {
		border-radius: 7px;
		border-bottom: 1px solid var(--neutral-40040);
	}
	&:hover {
		.search-placeholder {
			color: var(--neutral-800);
			> strong {
				font-weight: normal;
			}
		}
	}
	&:hover {
		.search-bar-icon {
			color: var(--neutral-800);
		}
	}
	.search-bar-interior {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
	.search-input-container {
		flex-grow: 1;
		display: flex;
		align-items: center;
		padding: 0 16px;
	}
	.search-placeholder {
		display: inline-flex;
		align-items: center;
		cursor: pointer;
		flex-shrink: 0;
		text-align: center;
		font-size: 14px;
		color: var(--neutral-500);
		max-width: 300px;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		> strong {
			margin-left: 4px;
			color: var(--neutral-800);
			font-weight: normal;
		}
	}

	.search-input {
		flex-grow: 1;
		border: 0;
		font-size: 14px;
		background: transparent;
		font-family: Avenir-Medium;
		caret-color: var(--teal-300);
		color: var(--neutral-800);
		&:focus {
			outline: none;
		}
	}
	.search-bar-icon {
		margin-right: 8px;
	}

	.clear-search {
		cursor: pointer;
		font-size: 15px;
		margin-right: 16px;
		&:hover {
			color: var(--teal-300);
		}
	}

	.filter-count {
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 2px 5px 2px 5px;
		background-color: var(--neutral-100);
		color: var(--neutral-500);
		font-family: Avenir-Medium;
		font-size: 13px;
		border-radius: 23px;
		border: solid 1px var(--neutral-200);
		margin-right: 8px;
		.pronto-icon {
			font-size: 16px;
			margin-right: 4px;
		}
	}
}
.dropdown-overlay {
	width: 100%;
	position: absolute;
	z-index: 100;
	top: $bar-height;
	left: 0;
	border-radius: 0 0 7px 7px;
	box-shadow: 0 4px 14px 0px rgba(0, 0, 0, 0.13);
	clip-path: inset(0px -4px -4px -4px);
	max-height: 0;
	overflow: hidden;
	transition: all 0.3s ease-in-out;
	transition-property: max-height;
	border: 1px solid transparent;
	&.expanded {
		max-height: 400px;
	}
	&.expanded,
	&.transitioning {
		margin-top: -1px;
		background: var(--neutral-0);
		width: calc(100% + 2px);
		left: -1px;
		border-top: 0;
		border-color: var(--neutral-40040);
		.filter-container,
		.recent-searches {
			border-color: var(--neutral-200);
		}
	}
	.filter-container {
		@include flex(8px);
		padding: 0 16px;
		width: 100%;
		height: 55px;
		border-top: 1px solid transparent;
		overflow-x: auto;
		white-space: nowrap;
	}
	.suggestions {
		padding: 8px;
		border-top: 1px solid var(--neutral-200);
	}
	.recent-searches {
		padding: 8px;
		border-top: 1px solid transparent;
		.recent-searches-label {
			font-size: 12px;
			color: var(--neutral-500);
			margin-bottom: 2px;
			padding: 0 8px;
		}
		.recent-search {
			@include flex-spread;
			@include radius;
			cursor: pointer;
			padding: 8px;
			font-size: 14px;
			.remove-recent {
				opacity: 0;
				&:hover {
					color: var(--blue-300);
				}
			}
			&:hover {
				background: var(--blue-100);
				.remove-recent {
					opacity: 1;
				}
			}
		}
	}
	&.datedropdown-open {
		z-index: 4;
	}
	.filter-search-suggestion,
	.current-chat-suggestion {
		@include flex(12px);
		padding: 8px;
		@include radius;
		cursor: pointer;
		overflow: hidden;
		font-size: 14px;
		&:hover {
			background: var(--blue-100);
		}
		.pronto-icon {
			font-size: 24px;
			color: var(--neutral-500);
		}
	}

	.filter-search-suggestion {
		.pronto-icon {
			font-size: 22px;
		}
	}
}
.dropdown-backdrop {
	position: fixed;
	z-index: 4;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	cursor: default;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.search-bar-container {
		background: var(--neutral-0);
		.search-bar {
			background: var(--neutral-100);
		}
		.recent-searches {
			.recent-search {
				&:hover {
					background: var(--neutral-300);
				}
			}
		}
		.filter-search-suggestion,
		.current-chat-suggestion {
			&:hover {
				background: var(--neutral-300);
			}
		}
	}
}
</style>
